import React, { useEffect } from 'react'
import PropTypes from 'prop-types'

import { Controller, useForm } from 'react-hook-form'
import {
  Box,
  FormControlLabel,
  Grid,
  makeStyles,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { Autocomplete } from '@material-ui/lab'

import { Label } from 'components'

import useSnackbar from 'hooks/useSnackbar'
import useConsent from 'hooks/useConsent'
import useFetch from 'hooks/useFetch'

import helpers from 'helpers'
import * as service from 'service'
import constants from 'constants/index'

import schema from './schema'
import styles from './styles'

const useStyles = makeStyles(styles)

const ControlledRadioGroup = ({ value, onChange, children }) => {
  const handleChange = (event) => {
    onChange(parseInt(event.target.value))
  }

  return (
    <RadioGroup value={parseInt(value || 0)} onChange={handleChange}>
      {children}
    </RadioGroup>
  )
}

const Settings = ({ setTokenPrivacyPortal, tokenPrivacyPortal }) => {
  const {
    nextStep,
    toNextStep,
    setToNextStep,
    dataConsentForm,
    setDataConsentForm,
    setNextDisabled,
    setIsConsentLoading,
  } = useConsent()
  const snackbar = useSnackbar()
  const classes = useStyles()

  const { response } = useFetch(service.dponet.users.get, {
    perPage: 10000,
    minimal: true,
    active: true,
    userCompanyStatus: constants.userCompanies.ACTIVE,
    // DPO terceiro é recusado pelo backend ao submeter, então não deve aparecer no autocomplete.
    excludeOutsourcedDpo: true,
  })

  const users = response?.data?.users || []

  const { control, errors, handleSubmit, watch, setValue } = useForm({
    validationSchema: schema,
    defaultValues: {
      responsibleUsers: [],
      expirationType: 0,
      dueDate: null,
      expirationTime: 1,
      expirationUnit: constants.consent.DAY_UNIT,
      expiration: false,
      emailConfirmation: dataConsentForm?.emailConfirmation || false,
      emailResponsible: dataConsentForm?.emailResponsible || false,
    },
  })

  const expiration = watch('expiration')
  const formExpirationChoice = parseInt(watch('expirationType'))

  useEffect(() => {
    if (!response) return

    setValue(
      'responsibleUsers',
      users.filter((user) =>
        (dataConsentForm?.responsibleIds || []).includes(user.id),
      ),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response])

  const onSubmit = async (data) => {
    setIsConsentLoading(true)

    const components = dataConsentForm.components
    data.dueDate = helpers.formatters.date(data.dueDate)

    const { responsibleUsers, ...restData } = data

    try {
      const mergedData = {
        ...dataConsentForm,
        ...restData,
        responsibleIds: (responsibleUsers || []).map((user) => user.id),
        kind: constants.consent.REAL_KIND,
      }

      setDataConsentForm({ ...mergedData, components })
      setNextDisabled(true)

      let response
      if (!tokenPrivacyPortal) {
        response = await service.dponet.consent.create({
          consentForm: {
            ...mergedData,
            components: JSON.stringify(components),
          },
        })
      }

      setTokenPrivacyPortal(response?.data?.token || tokenPrivacyPortal)
      setNextDisabled(false)

      nextStep()
      setToNextStep(false)
    } catch (error) {
      const revertedData = {
        ...dataConsentForm,
        components: components,
      }
      setDataConsentForm(revertedData)

      snackbar.open({
        message:
          helpers.formatters.errorMessage(error, false) ||
          error?.error?.message,
        variant: 'error',
      })
    } finally {
      setIsConsentLoading(false)
    }
  }

  useEffect(() => {
    if (toNextStep) {
      handleSubmit(onSubmit)()

      if (errors) setToNextStep(false)
    }
    // eslint-disable-next-line
  }, [toNextStep])

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      id={constants.consent.NEW_CONSENT_FORM_DRIVER_STEP_4}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box>
            <Typography variant="subtitle2">Atribuir Responsáveis</Typography>
            <Typography color="secondary" className={classes.lineDescription}>
              É necessário atribuir ao menos um responsável para gerenciar as
              revogações dos consentimentos de titulares de dados.
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box className={classes.descriptionBox}>
            <Typography variant="subtitle2">
              Encaminhar para os responsáveis da organização
            </Typography>
            <Typography>
              Indique quem irá operacionalizar as revogações nos consentimentos
              de titulares de dados dentro da sua organização. Todos os
              responsáveis recebem as mesmas notificações.
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Label title="Responsáveis *">
            <Controller
              as={
                <Autocomplete
                  multiple
                  fullWidth
                  filterSelectedOptions
                  options={users}
                  getOptionLabel={(option) => option.name || ''}
                  getOptionSelected={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Selecione os responsáveis"
                      error={!!errors.responsibleUsers}
                      helperText={errors?.responsibleUsers?.message}
                    />
                  )}
                />
              }
              control={control}
              name="responsibleUsers"
              onChange={([, data]) => data}
              mode="onChange"
            />
          </Label>
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="expiration"
            control={control}
            onChange={([event]) => event.target.checked}
            as={
              <FormControlLabel
                label="Ativar vencimento"
                control={<Switch color="primary" />}
              />
            }
          />
        </Grid>
        {expiration && (
          <Grid item xs={12}>
            <Controller
              as={
                <ControlledRadioGroup>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <FormControlLabel
                        value={0}
                        control={<Radio size="small" />}
                        label="Por data de expiração do formulário"
                      />
                    </Grid>

                    {formExpirationChoice === 0 && (
                      <Grid item xs={12}>
                        <Controller
                          as={
                            <KeyboardDatePicker
                              fullWidth
                              format="DD/MM/yyyy"
                              label="Escolha a data de vencimento"
                              inputVariant="outlined"
                              error={!!errors?.dueDate}
                              helperText={errors?.dueDate?.message}
                              disablePast
                              clearable
                            />
                          }
                          control={control}
                          onChange={([date]) => date}
                          name="dueDate"
                          mode="onBlur"
                        />
                      </Grid>
                    )}
                  </Grid>

                  <Box mt={2}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <FormControlLabel
                          value={1}
                          control={<Radio size="small" />}
                          label="Por tempo de expiração de assinatura"
                        />
                      </Grid>

                      {formExpirationChoice === 1 && (
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Controller
                              as={
                                <TextField
                                  error={!!errors?.expirationTime}
                                  helperText={errors?.expirationTime?.message}
                                  label="Escolha o tempo de validade"
                                  variant="outlined"
                                  fullWidth
                                />
                              }
                              control={control}
                              name="expirationTime"
                              mode="onBlur"
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <Controller
                              as={
                                <TextField
                                  select
                                  label="Escolha a unidade do tempo de validade"
                                  fullWidth
                                  error={!!errors.expirationUnit}
                                  helperText={errors.expirationUnit?.message}
                                >
                                  <MenuItem value={constants.consent.DAY_UNIT}>
                                    Dias
                                  </MenuItem>
                                  <MenuItem
                                    value={constants.consent.MONTH_UNIT}
                                  >
                                    Meses
                                  </MenuItem>
                                  <MenuItem value={constants.consent.YEAR_UNIT}>
                                    Anos
                                  </MenuItem>
                                </TextField>
                              }
                              control={control}
                              name="expirationUnit"
                              mode="onBlur"
                            />
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </ControlledRadioGroup>
              }
              control={control}
              name="expirationType"
              mode="onBlur"
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <Controller
            name="emailConfirmation"
            control={control}
            onChange={([event]) => event.target.checked}
            as={
              <FormControlLabel
                label="Ativar a confirmação de identidade"
                control={<Switch color="primary" />}
              />
            }
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="emailResponsible"
            control={control}
            onChange={([event]) => event.target.checked}
            as={
              <FormControlLabel
                label="Ativar o envio de e-mail ao responsável pela solicitação"
                control={<Switch color="primary" />}
              />
            }
          />
        </Grid>
      </Grid>
      <Box mt={3}>
        <Grid item xs={12}>
          <Box className={classes.warningBox}>
            <Typography>
              <strong>Atenção:</strong> Não será possível editar a data de
              vencimento após a coleta de assinaturas.
            </Typography>
          </Box>
        </Grid>
      </Box>
    </form>
  )
}

Settings.propTypes = {
  setTokenPrivacyPortal: PropTypes.func.isRequired,
}

export default Settings
